import {
  type AgentSubscriber,
  type Message
} from "@ag-ui/client";
import { AiChatAssistantTranslation, DEFAULT_CONTEXT } from "app/components/ai-chat-assistant/constants";
import { AiChatAssistantRole, CaseDraftPayload, CaseDraftSnapShot, ChatMessage } from "app/components/ai-chat-assistant/types";
import { toDraftQuery } from "app/components/ai-chat-assistant/utils/to-draft-query";
import { ApplicationState } from "app/types-store";
import { useCallback, useState, useRef, useEffect, RefObject, DependencyList } from "react";
import * as React from 'react';
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { actionCreators } from "app/components/ai-chat-assistant/actions";
import { useRunGuard } from "app/components/ai-chat-assistant/hooks/use-run-guard";
import { useCaseDraftConfirmation } from "app/components/ai-chat-assistant/hooks/use-case-draft-confirmation";
import { createId, useChatMessages } from "app/components/ai-chat-assistant/hooks/use-ai-chat-assistant-messages";
import { useAiChatAssistantSelectors } from "app/components/ai-chat-assistant/hooks/use-ai-chat-assistant-selectors";
import { useTranslation } from "app/infrastructure/translate/hooks";
import { STATE } from "app/constants";

type StreamState = {
  fullText: string;
};

type CreateAgUiSubscriberArgs = {
  requestId: number;
  aiChatAssistantMessageId: string;
  streamState: StreamState;
  isActiveRun: (runId: number) => boolean;
  updateMessage: (id: string, content: string) => void;
  handleStateSnapshot: (event: { type: string, snapshot?: CaseDraftSnapShot }) => void;
  handleConfirmedDraft: () => Promise<void>;
  translation: Record<string, string>;
};

type SendMessageArgs = {
  inputText: string;
  setInputText: (text: string) => void;
  running: boolean;
  tenant: string;
  contextValue: string;
  threadId: string;
  messagesRef: React.RefObject<ChatMessage[]>;
  appendMessages: (messages: ChatMessage[]) => void;
  updateMessage: (id: string, content: string) => void;
  startRun: () => { runId: number; abortController: AbortController };
  isActiveRun: (runId: number) => boolean;
  resetDraftConfirmation: () => void;
  handleStateSnapshot: (event: { type: string, snapshot?: CaseDraftSnapShot }) => void;
  handleConfirmedDraft: () => Promise<void>;
  hasConfirmedDraft: () => boolean;
  dispatch: ThunkDispatch<ApplicationState, any, any>;
  translation: Record<string, string>;
};

type ExecuteAgUiRunArgs = {
  dispatch: ThunkDispatch<ApplicationState, any, any>;
  tenant: string;
  context: string;
  threadId: string;
  outboundMessages: Message[];
  subscriber: AgentSubscriber;
  isActiveRun: (id: number) => boolean;
  requestId: number;
  streamState: StreamState;
  aiChatAssistantMessage: { id: string };
  hasConfirmedDraft: () => boolean;
  updateMessage: (id: string, text: string) => void;
  translation: Record<string, string>;
  abortController: AbortController;
};

function toAgentMessage(message: ChatMessage): Message {
  switch (message.role) {
    case AiChatAssistantRole.User:
      return {
        id: message.id,
        role: AiChatAssistantRole.User,
        content: message.content
      };

    case AiChatAssistantRole.Assistant:
      return {
        id: message.id,
        role: AiChatAssistantRole.Assistant,
        content: message.content
      };
    default:
      return null;
  }
}

function createUserMessage(content: string): ChatMessage {
  return {
    id: createId(),
    role: AiChatAssistantRole.User,
    content
  };
}

function createAiChatAssistantMessage(content = ""): ChatMessage {
  return {
    id: createId(),
    role: AiChatAssistantRole.Assistant,
    content
  };
}

function useAutoScroll(
  endRef: RefObject<HTMLDivElement | null>,
  deps: DependencyList
) {
  useEffect(() => {
    endRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end"
    });
  }, deps);
}

function createAgUiSubscriber({
  requestId,
  aiChatAssistantMessageId,
  streamState,
  isActiveRun,
  updateMessage,
  handleStateSnapshot,
  handleConfirmedDraft,
  translation
}: CreateAgUiSubscriberArgs): AgentSubscriber {
  return {
    onStateSnapshotEvent: ({ event }) => {
      handleStateSnapshot(event as { type: string; snapshot?: CaseDraftSnapShot });
    },

    onTextMessageStartEvent: () => {
      if (streamState.fullText.length > 0) {
        streamState.fullText += "\n\n";
      }
    },

    onTextMessageContentEvent: ({ event }) => {
      if (!isActiveRun(requestId)) {
        return;
      }

      streamState.fullText += event.delta ?? "";
      updateMessage(aiChatAssistantMessageId, streamState.fullText);
    },

    onRunFailed: () => {
      if (!isActiveRun(requestId)) {
        return;
      }

      updateMessage(
        aiChatAssistantMessageId,
        translation.textAiChatAssistantEndpointError
      );
    },

    onRunFinishedEvent: async () => {
      if (!isActiveRun(requestId)) {
        return;
      }

      await handleConfirmedDraft();
    }
  };
}

function validateAndPrepareMessage(
  value: string,
  running: boolean,
  tenant: string | null
): { valid: boolean; requestId?: string } {
  if (!value || running) {
    return { valid: false };
  }

  if (!tenant) {
    return { valid: false };
  }

  return { valid: true };
}

function buildOutboundMessages(
  messagesRef: RefObject<ChatMessage[]>,
  userMessage: ChatMessage
): Message[] {
  return [
    ...messagesRef.current,
    userMessage
  ].map(toAgentMessage);
}

async function executeAgUiRun({
  dispatch,
  tenant,
  context,
  threadId,
  outboundMessages,
  subscriber,
  isActiveRun,
  requestId,
  streamState,
  aiChatAssistantMessage,
  hasConfirmedDraft,
  updateMessage,
  translation,
  abortController
}: ExecuteAgUiRunArgs): Promise<void> {
  await dispatch(
    actionCreators.executeAiAssistantRun({
      tenant,
      context,
      threadId,
      messages: outboundMessages,
      subscriber,
      abortController
    })
  );

  if (!isActiveRun(requestId)) {
    return;
  }

  if (!streamState.fullText.trim() && !hasConfirmedDraft()) {
    updateMessage(aiChatAssistantMessage.id, translation.textNoResponse);
  }
}

function useSendMessage({
  inputText,
  setInputText,
  running,
  tenant,
  contextValue,
  threadId,
  messagesRef,
  appendMessages,
  updateMessage,
  startRun,
  isActiveRun,
  resetDraftConfirmation,
  handleStateSnapshot,
  handleConfirmedDraft,
  hasConfirmedDraft,
  dispatch,
  translation
}: SendMessageArgs) {
  return useCallback(async () => {
    const value = inputText.trim();

    const userMessage = createUserMessage(value);
    const aiChatAssistantMessage = createAiChatAssistantMessage();

    const validation = validateAndPrepareMessage(
      value,
      running,
      tenant
    );

    if (!validation.valid) {
      updateMessage(aiChatAssistantMessage.id, translation.textAiChatAssistantEndpointError);
      return;
    }

    const { runId: requestId, abortController } = startRun();
    setInputText("");
    appendMessages([userMessage, aiChatAssistantMessage]);
    resetDraftConfirmation();

    const outboundMessages = buildOutboundMessages(messagesRef, userMessage);

    const streamState: StreamState = {
      fullText: ""
    };

    const subscriber = createAgUiSubscriber({
      requestId, aiChatAssistantMessageId: aiChatAssistantMessage.id, streamState, isActiveRun,
      updateMessage, handleStateSnapshot, handleConfirmedDraft, translation
    });

    await executeAgUiRun({
      dispatch, 
      tenant, 
      context: contextValue,
      threadId,
      outboundMessages,
      subscriber, isActiveRun, requestId, streamState,
      aiChatAssistantMessage, hasConfirmedDraft, updateMessage, translation, abortController
    });
  }, [
    inputText, running, tenant, contextValue, threadId, messagesRef, appendMessages, updateMessage,
    startRun, isActiveRun, resetDraftConfirmation, handleStateSnapshot, handleConfirmedDraft,
    hasConfirmedDraft, dispatch, setInputText, translation
  ]);
}

function useInitializeHookDependencies() {
  const dispatch: ThunkDispatch<ApplicationState, any, any> = useDispatch();
  const [inputText, setInputText] = useState("");
  const endRef = useRef<HTMLDivElement | null>(null);

  const { enableChatAssistant: chatAssistant, caseDraftCreation, tenant, running, createDraft } = useAiChatAssistantSelectors();
  const translation = useTranslation(STATE.aiChatAssistant, AiChatAssistantTranslation);

  const contextValue = DEFAULT_CONTEXT;
  const {
    messages,
    messagesRef,
    threadId,
    appendMessages,
    updateMessage,
    clearMessages
  } = useChatMessages();

  const {
    startRun,
    isActiveRun,
    cancelCurrentRun,
    resetRunState
  } = useRunGuard();

  const {
    resetDraftConfirmation,
    handleStateSnapshot,
    consumeConfirmedDraft,
    consumeLatestDraft,
    hasConfirmedDraft,
    canRegisterCase
  } = useCaseDraftConfirmation();

  return {
    dispatch, inputText, setInputText, endRef, chatAssistant, caseDraftCreation, tenant, running, contextValue,
    messages, messagesRef, threadId, appendMessages, updateMessage, clearMessages,
    startRun, 
    isActiveRun, 
    cancelCurrentRun, 
    resetDraftConfirmation, 
    handleStateSnapshot, 
    consumeConfirmedDraft, 
    consumeLatestDraft, 
    hasConfirmedDraft,
    canRegisterCase,
    translation,
    resetRunState,
    createDraft
  };
}

function useDraftHandler(
  consumeDraft: () => CaseDraftPayload | null,
  dispatch: ThunkDispatch<ApplicationState, any, any>,
  updateMessage: (id: string, text: string) => void, 
  translation: Record<string, string>) {
  return useCallback(async () => {
    const draft = consumeDraft();
   
    if (!draft) {
      return;
    }
    const assistantMessage = createAiChatAssistantMessage();
    const caseDraftParameters = toDraftQuery(draft);
    const caseTypeId = draft?.caseType?.v;

    if (!caseTypeId) {
      return;
    }
    updateMessage(assistantMessage.id, translation.textCreatingDraft);
    await dispatch(
      actionCreators.createCaseDraft(
        caseDraftParameters,
        caseTypeId
      )
    ); 
  }, [consumeDraft, dispatch]);
}

function useKeyDownHandler(send: () => void, inputText: string, running: boolean, setInputText: (text: string) => void) {
  return useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!running && inputText.trim()) {
          const target = e.currentTarget;
          send();
          target?.blur();
        }
      }
    },
    [send, inputText, running, setInputText]
  );
}

export function useAiChatAssistantHooks() {
  const {
    dispatch,
    inputText,
    setInputText,
    endRef,
    chatAssistant,
    caseDraftCreation,
    tenant,
    running,
    contextValue,
    messages,
    messagesRef,
    threadId,
    appendMessages,
    updateMessage,
    clearMessages,
    startRun,
    isActiveRun,
    cancelCurrentRun,
    resetDraftConfirmation,
    handleStateSnapshot,
    hasConfirmedDraft,
    consumeConfirmedDraft,
    consumeLatestDraft,
    canRegisterCase,
    translation,
    resetRunState,
    createDraft
  } = useInitializeHookDependencies();

  useAutoScroll(endRef, [messages, running]);

  const handleConfirmedDraft = useDraftHandler(consumeConfirmedDraft, dispatch, updateMessage, translation);
  const registerCase = useDraftHandler(consumeLatestDraft, dispatch, updateMessage, translation);

  const send = useSendMessage({
    inputText, setInputText, running, tenant, contextValue, threadId, messagesRef, appendMessages, updateMessage,
    startRun, isActiveRun, resetDraftConfirmation, handleStateSnapshot, handleConfirmedDraft, hasConfirmedDraft, dispatch,
    translation
  });

  const clearChat = useCallback(() => {
    cancelCurrentRun();
    resetDraftConfirmation();
    clearMessages();
    resetRunState();
    setInputText("");
    dispatch(actionCreators.abortAiAssistantRun());
  }, [cancelCurrentRun, resetDraftConfirmation, clearMessages, resetRunState, setInputText]);

  const abortChat = useCallback(() => {
    cancelCurrentRun();
    resetDraftConfirmation();
    dispatch(actionCreators.abortAiAssistantRun());
  }, [cancelCurrentRun, resetDraftConfirmation, dispatch]);

  const onKeyDown = useKeyDownHandler(send, inputText, running, setInputText);

  return {
    chatAssistant,
    caseDraftCreation,
    running,
    messages,
    inputText,
    setInputText,
    send,
    onKeyDown,
    endRef,
    clearChat,
    abortChat,
    translation,
    hasConfirmedDraft,
    canRegisterCase,
    registerCase,
    createDraft
  };
}
